import type { CSSProperties } from 'react';
import { wedgeExactRole } from './wedgeRoles.js';

export type WedgeFloatingLook = 'floating-meeting-chip' | 'floating-meeting-listen' | 'floating-command-close' | 'floating-command-row'
  | 'floating-bilingual-chip' | 'floating-bilingual-show' | 'floating-business-action';
export const isWedgeFloatingLook = (look: string | undefined): look is WedgeFloatingLook => !!look && look.startsWith('floating-');

const center: CSSProperties = { display: 'flex', alignItems: 'center' };

// Released floating chrome: the folded next-meeting chip and its Listen pill, and the command palette close mark and result row.
// None draws a transparent hit area. The caller sets the placement and the paint that varies (chip fill, edge and shadow; row fill, outline and entrance).
export function wedgeFloatingStyle(look: WedgeFloatingLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'floating-meeting-chip') return {
    position: 'absolute', height: 44, borderRadius: 999,
    backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)',
    ...center, gap: 8, cursor: 'pointer', fontFamily: r('font'), zIndex: 40,
    animation: `prepFadeUp 240ms ${r('ease')}`, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  };
  if (look === 'floating-meeting-listen') return {
    height: 44, padding: '0 14px', background: r('label'), color: r('bg'), border: 0, borderRadius: 999,
    ...center, gap: 5, cursor: 'pointer', fontFamily: r('font'), fontSize: 12.5, fontWeight: 600, flexShrink: 0,
  };
  if (look === 'floating-command-close') return {
    background: r('bgElev'), border: 0, borderRadius: 999, width: 24, height: 24,
    ...center, justifyContent: 'center', cursor: 'pointer', color: r('labelSec'),
  };
  // Bilingual panel controls: the buyer-language chip and the full width Show in Spanish button. The caller sets the fill, edge colour, ink and busy opacity.
  if (look === 'floating-bilingual-chip' || look === 'floating-bilingual-show') {
    const base: CSSProperties = {
      background: 'transparent', border: `0.5px solid ${r('separator')}`, borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
      color: r('label'), fontSize: 12, fontWeight: 600, fontFamily: r('font'), WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', minHeight: 32,
    };
    return look === 'floating-bilingual-chip' ? { ...base, borderRadius: 999, padding: '6px 12px' } : { ...base, width: '100%', marginTop: 10 };
  }
  // Confirm business card actions: a 9.5px mono uppercase control. The caller sets the fill, edge, ink, cursor and busy opacity.
  if (look === 'floating-business-action') return {
    padding: '6px 10px', minHeight: 32, borderRadius: 8, fontFamily: r('mono'), fontSize: 9.5, fontWeight: 700, letterSpacing: 0.08,
    textTransform: 'uppercase', WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  };
  return {
    width: '100%', ...center, gap: 11, padding: '11px 10px', borderRadius: 13, border: 0,
    cursor: 'pointer', textAlign: 'left', fontFamily: r('font'),
  };
}
